import React, { useMemo, useState, useEffect } from 'react';
import {
  FaExclamationTriangle,
  FaRedo,
  FaPlusCircle,
  FaMinusCircle,
  FaArrowRight,
  FaUser,
  FaFilter,
  FaSyncAlt,
} from 'react-icons/fa';
import { Badge, Button } from './Primitives';
import { changeActionMeta } from '../utils/constants';
import {
  formatDateTime,
  formatFieldValue,
  originMeta,
  reconcileAttributeItems,
  summarizeChangeItems,
  CHANGE_SOURCE_ORDER,
} from '../utils/format';

// Sorgenti preselezionate: tutte tranne "attributes" (rumorose per ricensimento).
const DEFAULT_OFF_SOURCES = new Set(['attributes']);

const sourceLabel = (source) => originMeta(source)?.label || source;

/** Valore di campo troncato con valore completo accessibile via title. */
function FieldValue({ value }) {
  const { text, isEmpty } = formatFieldValue(value);
  return (
    <span
      title={text}
      className={`inline-block max-w-[16rem] truncate align-bottom ${
        isEmpty
          ? 'italic text-gray-400 dark:text-gray-500'
          : 'text-gray-700 dark:text-gray-200'
      }`}
    >
      {text}
    </span>
  );
}

function ChangedFields({ item }) {
  const fields = Array.isArray(item.changedFields) ? item.changedFields : [];

  if (item.action === 'DELETE') {
    return <p className="text-xs text-gray-500 dark:text-gray-400">Entità rimossa.</p>;
  }
  if (item.action === 'CREATE') {
    return <p className="text-xs text-gray-500 dark:text-gray-400">Entità aggiunta.</p>;
  }
  if (fields.length === 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Record aggiornato; dettaglio dei valori non disponibile.
      </p>
    );
  }
  return (
    <ul className="space-y-1">
      {fields.map((f, i) => (
        <li key={`${f.name}-${i}`} className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-medium text-gray-600 dark:text-gray-300">
            {f.label || f.name}:
          </span>
          <FieldValue value={f.before} />
          <FaArrowRight size={9} className="text-gray-400" />
          <FieldValue value={f.after} />
        </li>
      ))}
    </ul>
  );
}

function DetailItem({ item, churn }) {
  const meta = changeActionMeta(item.action);
  return (
    <li
      className={`rounded-lg border p-2.5 ${
        churn
          ? 'border-dashed border-gray-300 bg-gray-50 opacity-70 dark:border-gray-600 dark:bg-gray-900/40'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {meta && <Badge className={meta.style}>{meta.label}</Badge>}
        {churn && (
          <Badge
            className="bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
            title="Delete + ricreazione dovuti al ricensimento: nessuna modifica reale"
          >
            <FaSyncAlt size={9} /> Ricensito
          </Badge>
        )}
        <span className="text-xs font-medium text-gray-800 dark:text-gray-100">
          {item.entityName || item.entityId || '—'}
        </span>
        <span className="ml-auto flex items-center gap-2 text-[11px] text-gray-400">
          {item.actor && item.actor !== 'NONE' && (
            <span className="inline-flex items-center gap-1">
              <FaUser size={9} /> {item.actor}
            </span>
          )}
          <span>{formatDateTime(item.at)}</span>
        </span>
      </div>
      <div className="mt-1.5">
        <ChangedFields item={item} />
      </div>
    </li>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-2" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-14 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-700/50" />
      ))}
    </div>
  );
}

/** Chip di filtro per tipo (sorgente). */
function SourceChip({ source, count, active, onToggle }) {
  const meta = originMeta(source);
  return (
    <button
      type="button"
      onClick={() => onToggle(source)}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        active
          ? `${meta?.style || 'bg-primary/10 text-primary'} border-transparent ring-1 ring-inset ring-current`
          : 'border-gray-300 text-gray-500 hover:border-primary hover:text-primary dark:border-gray-600 dark:text-gray-400'
      }`}
    >
      {sourceLabel(source)}
      <span className="tabular-nums opacity-80">{count}</span>
    </button>
  );
}

/**
 * Pannello dettaglio modifiche (contenuto lazy per una macrofunzione espansa).
 * Riceve lo stato dell'hook: { loading, error, data }.
 *
 * - suddivisione per TIPO con chip attivabili (default: tutti tranne attributi);
 * - netting del rumore da ricensimento sugli attributi (churn nascosto di
 *   default, con toggle per mostrarlo).
 */
export default function ChangeDetailPanel({ detail, onRetry }) {
  const items = useMemo(
    () => (detail?.data && Array.isArray(detail.data.items) ? detail.data.items : []),
    [detail],
  );

  // Conteggi per sorgente (per le chip). Gli attributi contano solo i "reali".
  const { sourceCounts, presentSources, attrReal, attrChurn } = useMemo(() => {
    const attributeItems = items.filter((i) => i.source === 'attributes');
    const { real, churn } = reconcileAttributeItems(attributeItems);
    const counts = new Map();
    for (const it of items) {
      if (it.source === 'attributes') continue;
      counts.set(it.source, (counts.get(it.source) || 0) + 1);
    }
    if (attributeItems.length > 0) counts.set('attributes', real.length);
    const present = CHANGE_SOURCE_ORDER.filter((s) => counts.has(s));
    return { sourceCounts: counts, presentSources: present, attrReal: real, attrChurn: churn };
  }, [items]);

  const presentKey = presentSources.join(',');
  const [selected, setSelected] = useState(null);
  const [showChurn, setShowChurn] = useState(false);

  // Default alla comparsa/ricarica del dettaglio: tutti tranne attributi.
  useEffect(() => {
    setSelected(new Set(presentSources.filter((s) => !DEFAULT_OFF_SOURCES.has(s))));
    setShowChurn(false);
  }, [presentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSet = useMemo(
    () => selected || new Set(presentSources.filter((s) => !DEFAULT_OFF_SOURCES.has(s))),
    // presentSources è rappresentato in forma stabile da presentKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, presentKey],
  );

  const toggleSource = (source) =>
    setSelected((prev) => {
      const base = prev || new Set(activeSet);
      const next = new Set(base);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });

  // Item effettivamente mostrati, raggruppati per tipo.
  const { groups, displayedSummary, attrChurnShown } = useMemo(() => {
    const nonAttr = items.filter((i) => i.source !== 'attributes');
    const attrSelected = activeSet.has('attributes');
    const effectiveAttr = attrSelected ? (showChurn ? [...attrReal, ...attrChurn] : attrReal) : [];
    const churnShown = attrSelected && showChurn;

    const all = [...nonAttr, ...effectiveAttr].filter((i) => activeSet.has(i.source));

    const grouped = new Map();
    for (const it of all) {
      if (!grouped.has(it.source)) grouped.set(it.source, []);
      grouped.get(it.source).push(it);
    }
    // Ordine sorgenti + ordinamento per data desc entro il gruppo.
    const ordered = CHANGE_SOURCE_ORDER.filter((s) => grouped.has(s)).map((s) => ({
      source: s,
      items: grouped
        .get(s)
        .slice()
        .sort((a, b) => String(b.at || '').localeCompare(String(a.at || ''))),
    }));

    return {
      groups: ordered,
      displayedSummary: summarizeChangeItems(all.filter((i) => !attrChurn.includes(i))),
      attrChurnShown: churnShown,
    };
  }, [items, activeSet, showChurn, attrReal, attrChurn]);

  if (!detail || detail.loading) {
    return (
      <div className="p-1">
        <LoadingSkeleton />
      </div>
    );
  }

  if (detail.error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
        <span className="flex items-center gap-2">
          <FaExclamationTriangle size={13} /> {detail.error}
        </span>
        <Button variant="ghost" icon={FaRedo} onClick={onRetry} className="!px-2 !py-1 text-xs">
          Riprova
        </Button>
      </div>
    );
  }

  const data = detail.data;
  if (!data) return null;

  const truncated = data.pagination?.truncated;
  const attrSelected = activeSet.has('attributes');

  return (
    <div className="space-y-3">
      {/* Riepilogo dei conteggi effettivamente mostrati */}
      <div className="flex flex-wrap items-center gap-2">
        {data.summary?.headline && (
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
            {data.summary.headline}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
            <FaPlusCircle size={9} /> {displayedSummary.created} aggiunti
          </Badge>
          <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
            {displayedSummary.updated} modificati
          </Badge>
          <Badge className="bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300">
            <FaMinusCircle size={9} /> {displayedSummary.deleted} rimossi
          </Badge>
        </span>
      </div>

      {/* Filtro per tipo */}
      {presentSources.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 dark:text-gray-400">
            <FaFilter size={10} /> Tipo:
          </span>
          {presentSources.map((s) => (
            <SourceChip
              key={s}
              source={s}
              count={sourceCounts.get(s) || 0}
              active={activeSet.has(s)}
              onToggle={toggleSource}
            />
          ))}
        </div>
      )}

      {/* Nota + toggle churn attributi */}
      {attrSelected && attrChurn.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-1.5 text-[11px] text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
          <span>
            Gli attributi vengono rigenerati dal ricensimento: mostrati solo quelli
            realmente inseriti, modificati o rimossi.
          </span>
          <button
            type="button"
            onClick={() => setShowChurn((v) => !v)}
            aria-pressed={attrChurnShown}
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-medium text-primary hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <FaSyncAlt size={10} />
            {attrChurnShown ? 'Nascondi ricensimenti' : `Mostra ricensimenti (${attrChurn.length})`}
          </button>
        </div>
      )}

      {truncated && (
        <div className="rounded-lg bg-amber-50 px-3 py-1.5 text-[11px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          Dettaglio parziale: sono presenti altre modifiche nel periodo.
        </div>
      )}

      {/* Gruppi per tipo */}
      {groups.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Nessuna modifica da mostrare per i tipi selezionati.
        </p>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => {
            const meta = originMeta(group.source);
            return (
              <div key={group.source}>
                <div className="mb-1.5 flex items-center gap-2">
                  {meta && <Badge className={meta.style}>{sourceLabel(group.source)}</Badge>}
                  <span className="text-[11px] text-gray-400">{group.items.length}</span>
                </div>
                <ul className="space-y-1.5">
                  {group.items.map((it) => (
                    <DetailItem
                      key={it.id}
                      item={it}
                      churn={group.source === 'attributes' && attrChurn.includes(it)}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
